/* KYO additions (v8): CTA price pill + purchase note sync, gallery arrows, carousels, UGC playback.
   Loaded before kyo.js so it hears the first kyo:price-change events. No dependencies. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const toCents = value => Number(String(value || '').replace(/[^\d]/g, '')) || 0;
  const planActive = form => Boolean(form.querySelector('[data-selling-plan]')?.value);

  function syncPurchase(form, detail) {
    const pill = form.querySelector('[data-kyo-cta-pill]');
    if (pill) {
      const priceElement = pill.querySelector('[data-kyo-cta-price]');
      const compareElement = pill.querySelector('[data-kyo-cta-compare]');
      const price = detail?.price || priceElement.textContent.trim();
      let compare = detail?.compare || '';
      // Shopify compare-at price for the selected bundle quantity (only rendered when compare_at_price > price).
      if (!compare && !planActive(form)) compare = form.querySelector('[data-bundle-quantity]:checked')?.dataset.kyoAutoCompare || '';
      if (price) priceElement.textContent = price;
      const showCompare = Boolean(compare) && toCents(compare) > toCents(price);
      compareElement.textContent = showCompare ? compare : '';
      compareElement.hidden = !showCompare;
    }
    const subsave = form.querySelector('.kyo-subsave');
    if (subsave) {
      const active = planActive(form);
      subsave.classList.toggle('is-selected', active);
      subsave.setAttribute('aria-pressed', String(active));
      const chosen = subsave.querySelector('[data-kyo-subsave-chosen]');
      const plan = form.querySelector('[data-subscription-plan].is-selected');
      if (chosen) { chosen.hidden = !active; chosen.textContent = active && plan ? `${plan.dataset.planLabel} · ${subsave.dataset.changeLabel || 'wijzig'}` : ''; }
    }
    // button text follows the choice: "[aantal] potten toevoegen" / "Start abonnement"
    const submit = form.querySelector('.product-submit');
    const label = submit?.querySelector('[data-cta-label]');
    if (label && !submit.disabled) {
      const quantity = Number(form.querySelector('[data-bundle-quantity]:checked')?.dataset.bundleQuantity || 1);
      const { defaultLabel, labelMulti, labelSubscription } = submit.dataset;
      label.textContent = planActive(form) && labelSubscription ? labelSubscription
        : quantity > 1 && labelMulti ? labelMulti.replace('[aantal]', quantity) : defaultLabel;
    }
    // Shopify's dynamic checkout button does not support selling plans
    const express = form.querySelector('[data-kyo-dynamic-checkout]');
    if (express) express.hidden = planActive(form);
    const note = form.querySelector('[data-kyo-purchase-note]');
    if (note) {
      const mode = note.dataset.kyoNoteMode || 'auto';
      const subscription = mode === 'auto' ? planActive(form) : mode === 'subscription';
      note.querySelector('[data-kyo-note="subscription"]')?.toggleAttribute('hidden', !subscription);
      note.querySelector('[data-kyo-note="onetime"]')?.toggleAttribute('hidden', subscription);
    }
  }

  // kyo.js clears/sets the selling plan right after updating prices, so read the state on the next tick.
  // Sticky bar choice mirrors the bundle radios of the main buy box (and vice versa).
  function syncStickyChoice(form) {
    const select = document.querySelector('[data-sticky-choice]');
    if (!select || form !== document.querySelector('[data-product-form]')) return;
    const checked = form.querySelector('[data-bundle-quantity]:checked');
    const subscription = select.querySelector('[data-sticky-subscription]');
    if (subscription) subscription.hidden = !!checked;
    select.value = checked ? checked.id : '';
  }
  document.addEventListener('change', event => {
    const select = event.target.closest('[data-sticky-choice]');
    if (!select || !select.value) return;
    const radio = document.getElementById(select.value);
    if (!radio) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('kyo:price-change', event => {
    const form = event.target.closest?.('[data-product-form]');
    if (form) setTimeout(() => { syncPurchase(form, event.detail); syncStickyChoice(form); }, 0);
  });

  function stepGallery(button) {
    const gallery = button.closest('[data-gallery]');
    if (!gallery) return;
    const count = gallery.querySelectorAll('[data-slide]').length;
    if (!count) return;
    const next = (Number(gallery.dataset.active || 0) + Number(button.dataset.kyoGalleryStep) + count) % count;
    const thumb = gallery.querySelector(`[data-thumb="${next}"]`);
    if (!thumb) return;
    thumb.click();
    const strip = thumb.parentElement;
    strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: reduced.matches ? 'auto' : 'smooth' });
  }

  function setupCarousel(root) {
    if (root.dataset.kyoCarouselReady) return;
    const track = root.querySelector('[data-kyo-track]');
    const dots = root.querySelector('[data-kyo-dots]');
    const prev = root.querySelector('[data-kyo-prev]');
    const next = root.querySelector('[data-kyo-next]');
    if (!track) return;
    root.dataset.kyoCarouselReady = 'true';
    const nav = dots?.closest('.kyo-carousel-nav');
    let frame = 0;

    const step = () => {
      const [first, second] = track.children;
      if (!first) return track.clientWidth || 1;
      return (second ? second.offsetLeft - first.offsetLeft : first.offsetWidth) || 1;
    };
    const pageCount = () => {
      const max = track.scrollWidth - track.clientWidth;
      return max <= 2 ? 1 : Math.round(max / step()) + 1;
    };
    const current = () => Math.min(Math.round(track.scrollLeft / step()), pageCount() - 1);
    const go = index => track.scrollTo({ left: Math.max(0, index) * step(), behavior: reduced.matches ? 'auto' : 'smooth' });

    function render() {
      const pages = pageCount();
      if (dots && dots.children.length !== pages) {
        dots.replaceChildren(...Array.from({ length: pages }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'kyo-carousel-dot';
          dot.setAttribute('aria-label', `${index + 1} / ${pages}`);
          dot.addEventListener('click', () => go(index));
          return dot;
        }));
      }
      const active = current();
      dots?.querySelectorAll('.kyo-carousel-dot').forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
      if (prev) prev.disabled = active <= 0;
      if (next) next.disabled = active >= pages - 1;
      if (nav) nav.hidden = pages <= 1;
    }

    prev?.addEventListener('click', () => go(current() - 1));
    next?.addEventListener('click', () => go(current() + 1));
    track.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(render); }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(render).observe(track);
    else window.addEventListener('resize', render);
    render();
  }

  function playUgc(button) {
    const media = button.closest('[data-kyo-ugc]');
    const video = media?.querySelector('video');
    if (!video) return;
    document.querySelectorAll('[data-kyo-ugc].is-playing video').forEach(other => { if (other !== video) other.pause(); });
    media.classList.add('is-playing');
    video.controls = true;
    video.muted = false;
    video.play().catch(() => {});
  }

  // Click-to-play video (review modal, Over KYO inline). The Shopify video waits in a <template>
  // and only enters the page on play, so nothing downloads before the click.
  function mountVideo(scope, stage) {
    const existing = stage.querySelector('video');
    if (existing) return existing;
    const template = scope.querySelector('template[data-kyo-video-template]');
    if (!template) return null;
    stage.append(template.content.cloneNode(true));
    const video = stage.querySelector('video');
    if (!video) return null;
    if (template.dataset.label) video.setAttribute('aria-label', template.dataset.label);
    video.controls = true;
    video.playsInline = true;
    video.preload = 'auto';
    return video;
  }
  const isFullscreen = video => document.fullscreenElement === video || video.webkitDisplayingFullscreen;
  // An inline video that scrolls out of view pauses (and stops buffering further ahead).
  const inlineVideoObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => entries.forEach(entry => {
    const video = entry.target.querySelector('video');
    if (!entry.isIntersecting && video && !isFullscreen(video)) video.pause();
  })) : null;
  function playInlineVideo(button) {
    const box = button.closest('[data-kyo-video]');
    const stage = box?.querySelector('[data-kyo-video-stage]');
    const video = stage && mountVideo(box, stage);
    if (!video) return;
    box.classList.add('is-playing');
    video.muted = false;
    video.play().catch(() => {});
    video.focus({ preventScroll: true });
    inlineVideoObserver?.observe(box);
  }
  function openVideoDialog(button) {
    const dialog = document.getElementById(button.dataset.kyoVideoOpen);
    const stage = dialog?.querySelector('[data-kyo-video-stage]');
    const video = stage && mountVideo(dialog, stage);
    if (!video || dialog.open) return;
    dialog.kyoOpener = button;
    dialog.showModal();
    video.muted = false;
    video.play().catch(() => {});
  }
  // Closing (button, Escape, backdrop) pauses and removes the video, then returns focus to the card.
  document.addEventListener('close', event => {
    const dialog = event.target;
    if (!dialog.matches?.('[data-kyo-video-dialog]')) return;
    const video = dialog.querySelector('[data-kyo-video-stage] video');
    if (video) { video.pause(); video.remove(); }
    dialog.kyoOpener?.focus({ preventScroll: true });
  }, true);
  // Only one video with sound at a time: starting one pauses the others (muted loops are left alone).
  document.addEventListener('play', event => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || video.muted) return;
    document.querySelectorAll('video').forEach(other => { if (other !== video && !other.paused && !other.muted) other.pause(); });
  }, true);

  // Buy-box rating (kyo-social-proof, source 'reviews'): filled from the KYO · Reviews section on the page,
  // so stars, average and count always match the published reviews.
  function applyReviewStats(stats) {
    const lang = document.documentElement.lang || 'nl';
    document.querySelectorAll('[data-kyo-review-proof]').forEach(proof => {
      const text = proof.querySelector('[data-kyo-proof-text]');
      const stars = proof.querySelector('[data-kyo-proof-stars]');
      const icon = proof.querySelector('[data-kyo-proof-icon]');
      const link = proof.querySelector('.kyo-proof__link');
      if (stats && stats.count > 0) {
        const rating = new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(stats.average);
        text.textContent = (stats.count === 1 ? proof.dataset.textOne : proof.dataset.textOther).replace('{rating}', rating).replace('{count}', stats.count);
        const meter = stars.querySelector('.kyo-stars');
        meter?.style.setProperty('--kyo-rating', `${Math.min(100, stats.average * 20)}%`);
        meter?.setAttribute('aria-label', `${rating} / 5`);
        stars.hidden = false;
        if (icon) icon.hidden = true;
      } else {
        text.textContent = proof.dataset.textEmpty;
        stars.hidden = true;
        if (icon) icon.hidden = false;
      }
      if (link && stats?.target) link.href = stats.target;
      proof.classList.remove('kyo-proof--pending');
    });
  }
  document.addEventListener('kyo:review-stats', event => applyReviewStats(event.detail));
  window.addEventListener('load', () => applyReviewStats(window.kyoReviewStats || null));

  // Routine timeline: the rail fills with the scroll position, steps switch on as the fill reaches their dot.
  const journeys = new Set();
  let journeyFrame = 0;
  function updateJourney(list) {
    if (!list.isConnected) { journeys.delete(list); return; }
    const steps = [...list.children];
    const marker = window.innerHeight * 0.58;
    const dots = steps.map(step => { const dot = step.querySelector('.kyo-journey__dot'); const box = dot.getBoundingClientRect(); return box.top + box.height / 2; });
    let current = -1;
    steps.forEach((step, index) => {
      const active = dots[index] <= marker;
      if (active) current = index;
      step.classList.toggle('is-active', active);
      const next = dots[index + 1];
      const fill = next === undefined ? 0 : Math.min(1, Math.max(0, (marker - dots[index]) / (next - dots[index])));
      step.style.setProperty('--kyo-seg', fill.toFixed(3));
    });
    steps.forEach((step, index) => step.classList.toggle('is-current', index === current));
  }
  function onJourneyScroll() {
    cancelAnimationFrame(journeyFrame);
    journeyFrame = requestAnimationFrame(() => journeys.forEach(updateJourney));
  }
  function setupJourney(list) {
    if (list.dataset.kyoJourneyReady || !list.hasAttribute('data-kyo-journey-animate') || reduced.matches) return;
    list.dataset.kyoJourneyReady = 'true';
    list.classList.add('is-live');
    journeys.add(list);
    updateJourney(list);
  }
  window.addEventListener('scroll', onJourneyScroll, { passive: true });
  window.addEventListener('resize', onJourneyScroll);
  reduced.addEventListener('change', () => {
    if (!reduced.matches) return;
    journeys.forEach(list => { list.classList.remove('is-live'); [...list.children].forEach(step => step.style.removeProperty('--kyo-seg')); });
    journeys.clear();
  });

  // Sticky gallery column: when it is taller than the viewport, pin it by its bottom edge so the videos stay in view
  // and end level with the buy box. The sticky order bar is kept clear.
  let mediaFrame = 0;
  function updateMediaColumns() {
    document.querySelectorAll('[data-kyo-media-col].kyo-media-col--sticky').forEach(column => {
      const bar = document.querySelector('[data-sticky-buy]');
      const barHeight = bar && !bar.hidden && !bar.classList.contains('is-collapsed') ? bar.offsetHeight : 0;
      const gap = 24;
      const top = Math.min(gap, window.innerHeight - column.offsetHeight - gap - barHeight);
      column.style.setProperty('--kyo-media-top', `${Math.round(top)}px`);
    });
  }
  const queueMediaColumns = () => { cancelAnimationFrame(mediaFrame); mediaFrame = requestAnimationFrame(updateMediaColumns); };
  window.addEventListener('resize', queueMediaColumns);
  window.addEventListener('scroll', queueMediaColumns, { passive: true });
  window.addEventListener('load', queueMediaColumns);

  document.addEventListener('click', event => {
    const galleryStep = event.target.closest('[data-kyo-gallery-step]');
    if (galleryStep) stepGallery(galleryStep);
    const faqMore = event.target.closest('[data-kyo-faq-more]');
    if (faqMore) {
      const faq = faqMore.closest('.faq');
      faq?.classList.add('is-expanded');
      faqMore.setAttribute('aria-expanded', 'true');
      faq?.querySelector('.kyo-faq-extra summary')?.focus();
    }
    const swapToggle = event.target.closest('.kyo-swap__toggle');
    if (swapToggle) {
      const card = swapToggle.closest('[data-kyo-swap]');
      const flipped = swapToggle.getAttribute('aria-expanded') !== 'true';
      swapToggle.setAttribute('aria-expanded', String(flipped));
      card.classList.toggle('is-flipped', flipped);
      const front = card.querySelector('.kyo-swap__front');
      const back = card.querySelector('.kyo-swap__back');
      [[front, flipped], [back, !flipped]].forEach(([face, hide]) => { face.toggleAttribute('inert', hide); face.setAttribute('aria-hidden', String(hide)); });
    }
    const subsave = event.target.closest('[data-kyo-subsave-open]');
    if (subsave) subsave.closest('[data-product-form]')?.querySelector('[data-subscription-open]')?.click();
    const ugc = event.target.closest('[data-kyo-ugc-play]');
    if (ugc) playUgc(ugc);
    const inlineVideo = event.target.closest('[data-kyo-video-play]');
    if (inlineVideo) playInlineVideo(inlineVideo);
    const videoOpen = event.target.closest('[data-kyo-video-open]');
    if (videoOpen) openVideoDialog(videoOpen);
    const videoDialog = event.target.closest('[data-kyo-video-dialog]');
    if (videoDialog && (event.target === videoDialog || event.target.closest('[data-kyo-video-close]'))) videoDialog.close();
  });

  // The offer block has its own buy button: hide the sticky order bar while it is in view.
  const offerObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => entry.target.classList.toggle('is-in-view', entry.isIntersecting));
    document.documentElement.classList.toggle('kyo-offer-in-view', !!document.querySelector('[data-kyo-offerbuy].is-in-view'));
    entries.forEach(entry => { if (entry.isIntersecting && !entry.target.dataset.kyoSeen) { entry.target.dataset.kyoSeen = '1'; window.kyoTrack?.('kyo_offer_view', {}); } });
  }, { threshold: 0.15 }) : null;

  // ---------- measurement: dataLayer + Shopify Customer Events (+ Clarity) ----------
  const track = (name, data = {}) => {
    try { (window.dataLayer = window.dataLayer || []).push({ event: name, ...data }); } catch (e) {}
    try { window.Shopify?.analytics?.publish?.(name, data); } catch (e) {}
    try { window.clarity?.('event', name); } catch (e) {}
  };
  window.kyoTrack = track;
  let stickySource = false;
  document.addEventListener('click', event => {
    if (event.target.closest('[data-sticky-submit]')) stickySource = true;
    const plan = event.target.closest('[data-subscription-plan]');
    if (plan && !plan.disabled) track('kyo_subscription_selected', { plan: plan.dataset.planLabel || '' });
    const swap = event.target.closest('.kyo-swap__toggle');
    if (swap) setTimeout(() => { if (swap.getAttribute('aria-expanded') === 'true') track('kyo_swap_open', { card: swap.closest('[data-kyo-swap]')?.querySelector('strong')?.textContent || '' }); }, 0);
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target.closest?.('[data-product-form]');
    if (!form) return;
    const quantity = Number(form.querySelector('[data-product-quantity]')?.value || 1);
    const plan = form.querySelector('[data-selling-plan]')?.value || '';
    const source = stickySource ? 'sticky_bar' : form.closest('[data-kyo-offerbuy]') ? 'offer_block' : 'buy_box';
    stickySource = false;
    track('kyo_add_to_cart', { quantity, subscription: Boolean(plan), source });
  }, true);
  document.addEventListener('play', event => {
    const video = event.target;
    if (video.closest?.('.kyo-story')) return;
    if (!video.dataset.kyoTracked) { video.dataset.kyoTracked = '1'; track('kyo_video_play', { location: video.closest('.shopify-section')?.id || '' }); }
  }, true);
  // funnel: which option is chosen, which questions are opened, how far people get (per section, once)
  document.addEventListener('change', event => {
    const choice = event.target.closest?.('[data-bundle-quantity]');
    if (choice?.checked) track('kyo_choice_select', { quantity: Number(choice.dataset.bundleQuantity), source: choice.closest('[data-kyo-offerbuy]') ? 'offer_block' : 'buy_box' });
  });
  document.addEventListener('toggle', event => {
    const item = event.target;
    if (item.open && item.matches?.('.faq details')) track('kyo_faq_open', { question: item.querySelector('summary')?.textContent.trim() || '', list: item.closest('.faq')?.id || '' });
  }, true);
  if ('IntersectionObserver' in window) {
    const sectionObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      sectionObserver.unobserve(entry.target);
      track('kyo_section_view', { section: entry.target.id.replace(/^shopify-section-(template--\d+__)?/, '') });
    }), { rootMargin: '0px 0px -40% 0px' });
    document.querySelectorAll('main > .shopify-section[id]').forEach(section => sectionObserver.observe(section));
  }
  const depths = [25, 50, 75, 100]; const reached = new Set();
  const progressBar = document.querySelector('[data-kyo-progress]');
  let progressFrame = 0;
  window.addEventListener('scroll', () => {
    cancelAnimationFrame(progressFrame);
    progressFrame = requestAnimationFrame(() => {
      const max = document.documentElement.scrollHeight - innerHeight;
      const pct = max > 0 ? Math.min(1, scrollY / max) : 0;
      if (progressBar) progressBar.style.transform = `scaleX(${pct})`;
      depths.forEach(d => { if (pct * 100 >= d - 1 && !reached.has(d)) { reached.add(d); track('kyo_scroll_depth', { percent: d }); } });
    });
  }, { passive: true });

  function init(scope = document) {
    scope.querySelectorAll('[data-kyo-carousel]').forEach(setupCarousel);
    scope.querySelectorAll('[data-kyo-journey]').forEach(setupJourney);
    scope.querySelectorAll('[data-kyo-offerbuy]').forEach(offer => offerObserver?.observe(offer));
    updateMediaColumns();
    if ('ResizeObserver' in window) scope.querySelectorAll('[data-kyo-media-col]').forEach(column => { if (!column.dataset.kyoObserved) { column.dataset.kyoObserved = 'true'; new ResizeObserver(queueMediaColumns).observe(column); } });
    scope.querySelectorAll('[data-product-form]').forEach(form => syncPurchase(form));
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:block:select', event => {
    const block = event.target;
    const track = block.closest('[data-kyo-track]');
    if (track) track.scrollTo({ left: block.offsetLeft, behavior: 'auto' });
    if (block.matches('details.kyo-info__item')) block.open = true;
    if (block.matches('.kyo-faq-extra')) block.closest('.faq')?.classList.add('is-expanded');
    const slide = block.closest('.kyo-gallery-slide');
    if (slide) slide.closest('[data-gallery]')?.querySelector(`[data-thumb="${slide.dataset.slide}"]`)?.click();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
